import { Component } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { logger } from "../utils/logger";
import { Global } from "../constants/global";

const ErrorDisplay = ({ error, errorInfo, onReset, onReload }) => {
  const theme = useTheme();
  const backgroundColor = theme.palette.background.default || "#F4F4F4";
  const textColor = theme.palette.text.primary || "#4A4A4A";
  const secondaryColor = theme.palette.text.secondary || "#8B99AF";
  const borderColor = theme.palette.divider || "rgba(0, 0, 0, 0.08)";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 3,
        textAlign: "center",
        backgroundColor: backgroundColor,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          color: textColor,
          mb: 2,
          fontFamily: "'Audiowide', sans-serif",
        }}
      >
        SYSTEM ANOMALY DETECTED
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: secondaryColor,
          mb: 4,
          maxWidth: 500,
          fontFamily: "'Play', sans-serif",
        }}
      >
        The Back Office terminal has encountered an unexpected runtime error.
        Please re-establish connection or return to the main dashboard.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={onReload}
          sx={{
            fontWeight: "bold",
            backgroundColor: textColor,
            color: "#FFFFFF",
            px: 4,
            "&:hover": {
              backgroundColor: "#333",
            },
          }}
        >
          Re-initialize Session
        </Button>

        <Button
          variant="outlined"
          onClick={onReset}
          sx={{
            borderColor: textColor,
            color: textColor,
            fontWeight: "bold",
            px: 4,
          }}
        >
          Return to Hub
        </Button>
      </Box>

      {process.env.VITE_ENV !== Global.PRD && error && (
        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: "#FFFFFF",
            borderRadius: 1,
            border: `1px solid ${borderColor}`,
            maxWidth: 800,
            width: "100%",
            textAlign: "left",
            overflow: "auto",
            maxHeight: "40vh",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              color: "#d32f2f",
              display: "block",
              mb: 1,
              fontWeight: "bold",
            }}
          >
            [DEBUG LOG]
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: 11,
              whiteSpace: "pre-wrap",
            }}
          >
            {error.toString()}
            {"\n\nComponent Stack:"}
            {errorInfo?.componentStack}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    logger.error("ErrorBoundary caught an error:", {
      error: error.toString(),
      stack: errorInfo.componentStack,
      url: window.location.href,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/";
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
